import React, { useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

// UI
import { Box, Flex, Heading } from '@chakra-ui/react'

// Component

import { authSelector } from '@/stores/auth/authSlice'

import { useDispatch } from 'react-redux'
import { Currency, VendorUploadStatus } from '../../constants'

import { useInitialTableState } from '@/components/Table/components/useInitialTableState'
import FullTable from '@/components/Table/FullTable/FullTable'
import { convertTableStateToQuery } from '@/utils/tableHelper'
import { createColumnHelper } from '@tanstack/react-table'

import { PriceCell, RawTextCell } from '@/components/Table/components/Cell'
import DateCell from '@/components/Table/components/Cell/DateCell'
import config from '@/config'
import { ANNOUNCEMENT_TYPES } from '@/features/announcements/announcement.constant'
import useAnnouncement from '@/features/announcements/useAnnouncement'
import useFile from '@/features/files/useFile.js'
import useOfferCostBudget from '@/features/offerCostBudgets/useOfferCostBudget'
import { showModal } from '@/stores/ui/modal/modalSlice'
import { MODAL_TYPES } from '@/utils/constants/modalTypes'
import moment from 'moment'

const RFQListPage = () => {
  const isProduction = config.mode === 'production'
  const { user } = useSelector(authSelector)

  const { useDownloadFile } = useFile()
  const downloadFile = useDownloadFile()

  const { t, i18n } = useTranslation()
  const dispatch = useDispatch()

  const { user: currentUser } = useSelector(authSelector)
  // Local State
  const [storedTableState, setStoredTableState] = useInitialTableState(`table_state:vendorQuotation`, {})
  const query = convertTableStateToQuery(storedTableState)

  // Hooks
  const { useFetchAnnouncementByType } = useAnnouncement()
  const { useFetchVendorRFQs } = useOfferCostBudget()
  const { data: vendorRFQResponse } = useFetchVendorRFQs(query)
  const { data: fetchAnnouncementResponse } = useFetchAnnouncementByType(ANNOUNCEMENT_TYPES.EXTERNAL)

  const announcementData = fetchAnnouncementResponse?.data

  const navigate = useNavigate()
  const columnHelper = createColumnHelper()

  useEffect(() => {
    if (!announcementData) return

    const today = moment()
    const isTodayBetweenPeriod =
      announcementData?.startDate &&
      announcementData?.endDate &&
      today.isSameOrAfter(moment(announcementData?.startDate), 'day') &&
      today.isSameOrBefore(moment(announcementData?.endDate), 'day')

    if (!isTodayBetweenPeriod) return

    // Check if announcement was already shown in this session
    const announcementKey = `announcement_shown_${announcementData._id}`

    const hasBeenShown = sessionStorage.getItem(announcementKey)

    if (hasBeenShown) return

    // Mark as shown and display the modal
    sessionStorage.setItem(announcementKey, 'true')

    dispatch(
      showModal({
        modalType: MODAL_TYPES.ANNOUNCEMENT,
        modalProps: {
          contentHtml: announcementData?.contentHtml,
        },
      })
    )
  }, [announcementData, user, dispatch])

  const handleGotoQuotation = (row) => {
    const { _id } = row.original

    const link = !isProduction ? '/external/RFQs/' + _id : '/RFQs/' + _id

    navigate(link)
  }
  const defaultColumns = useMemo(
    () => [
      columnHelper.accessor('scopeOfSupply', {
        header: () => <span>{t('VENDOR_QUOTATION_LIST_COLUMN_SCOPE_OF_SUPPLY')}</span>,
        cell: RawTextCell,
      }),
      columnHelper.accessor('rfqNumber', {
        header: () => <span>{t('VENDOR_QUOTATION_LIST_RFQ_NUMBER')}</span>,
        cell: RawTextCell,
      }),
      columnHelper.accessor('dueDate', {
        header: () => <span>{t('VENDOR_QUOTATION_LIST_COLUMN_SUBMISSION_DEADLINE')}</span>,
        cell: DateCell,
        meta: {
          filterVariant: 'date',
        },
      }),
      columnHelper.accessor('status', {
        header: () => <span>{t('VENDOR_QUOTATION_LIST_COLUMN_STATUS')}</span>,
        cell: ({ getValue }) => {
          const value = getValue() ?? ''
          return t(`VENDOR_UPLOAD_STATUS_${value.toUpperCase()}`)
        },
        meta: {
          filterVariant: 'select',
          filterOptions: Object.values(VendorUploadStatus).map((status) => {
            return {
              value: status,
              label: t('VENDOR_UPLOAD_STATUS_' + status.toUpperCase()),
            }
          }),
        },
      }),
      columnHelper.accessor('lastUploadDate', {
        header: () => <span>{t('VENDOR_QUOTATION_LIST_COLUMN_LAST_UPLOADED_DATE_TIME')} </span>,
        cell: DateCell,
        meta: {
          filterVariant: 'date',
        },
      }),

      columnHelper.accessor('lastUploadAmount', {
        header: () => <span>{t('VENDOR_QUOTATION_LIST_COLUMN_LAST_UPLOAD_AMOUNT')}</span>,
        cell: (cell) => <PriceCell {...cell} currency={cell.row.original?.currency ?? ''} />,
        meta: {
          filterVariant: 'range',
        },
      }),
      columnHelper.accessor('currency', {
        header: () => <span>{t('VENDOR_QUOTATION_LIST_COLUMN_CURRENCY')}</span>,
        cell: ({ getValue }) => {
          const value = getValue() ?? ''
          return value
        },
        meta: {
          filterVariant: 'select',
          filterOptions: Object.values(Currency).map((currency) => {
            return {
              value: currency,
              label: currency,
            }
          }),
        },
      }),
      columnHelper.accessor('projectStartDate', {
        cell: DateCell,
        header: () => <span>{t('VENDOR_QUOTATION_LIST_TENTATIVE_START_DATE')}</span>,
        meta: {
          filterVariant: 'date',
        },
      }),
      columnHelper.accessor('projectEndDate', {
        cell: DateCell,
        header: () => <span>{t('VENDOR_QUOTATION_LIST_EXPECTED_DELIVERY_DATE')}</span>,
        meta: {
          filterVariant: 'date',
        },
      }),
    ],
    [i18n.language]
  )

  if (!currentUser?.fullName) {
    return <>{t('LOADING')}</>
  }

  return (
    <>
      <Box shadow="md" borderWidth="1px" bg="white" w="100%" mb="auto">
        <Flex p={5} borderBottom="1px solid #d8dbe0">
          <Heading>{t('VENDOR_QUOTATION_LIST_TITLE')}</Heading>
        </Flex>

        <Box p={5} overflow={'scroll'}>
          <FullTable
            data={vendorRFQResponse?.data ? vendorRFQResponse.data : []}
            rowCount={vendorRFQResponse?.count}
            columnDef={defaultColumns}
            state={storedTableState}
            setState={setStoredTableState}
            handleRowClick={handleGotoQuotation}
          />
        </Box>
      </Box>
    </>
  )
}

export default RFQListPage
